import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, XCircle, AlertCircle, Loader2, FileText } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// ─── Payment Form (inside Elements provider) ────────────────────────────────
function PaymentForm({ invoiceNumber, totalAmount }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setSubmitting(false);
    } else {
      setSucceeded(true);
      setSubmitting(false);
    }
  };

  if (succeeded) {
    return <SuccessScreen invoiceNumber={invoiceNumber} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full h-12 text-base"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay $${totalAmount}`
        )}
      </Button>
    </form>
  );
}

// ─── Status Screens ──────────────────────────────────────────────────────────
function SuccessScreen({ invoiceNumber }) {
  return (
    <div className="text-center py-8 space-y-4">
      <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
      <h2 className="text-2xl font-semibold text-gray-900">Payment Successful</h2>
      <p className="text-gray-600">
        Invoice {invoiceNumber} has been paid. You will receive a confirmation email shortly.
      </p>
    </div>
  );
}

function PaidScreen({ invoiceNumber }) {
  return (
    <div className="text-center py-8 space-y-4">
      <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
      <h2 className="text-2xl font-semibold text-gray-900">Already Paid</h2>
      <p className="text-gray-600">
        Invoice {invoiceNumber} has already been paid. No further action is needed.
      </p>
    </div>
  );
}

function CancelledScreen({ invoiceNumber }) {
  return (
    <div className="text-center py-8 space-y-4">
      <XCircle className="h-16 w-16 text-gray-400 mx-auto" />
      <h2 className="text-2xl font-semibold text-gray-900">Invoice Cancelled</h2>
      <p className="text-gray-600">
        Invoice {invoiceNumber} has been cancelled and is no longer payable.
      </p>
    </div>
  );
}

function NoPaymentScreen({ businessName }) {
  return (
    <div className="text-center py-8 space-y-4">
      <AlertCircle className="h-16 w-16 text-amber-500 mx-auto" />
      <h2 className="text-2xl font-semibold text-gray-900">Online Payment Unavailable</h2>
      <p className="text-gray-600">
        Please contact {businessName || 'the business'} directly to arrange payment.
      </p>
    </div>
  );
}

// ─── Invoice Details Card ────────────────────────────────────────────────────
function InvoiceDetails({ invoice }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between text-sm text-gray-600">
        <span>Invoice Date</span>
        <span>{new Date(invoice.invoice_date).toLocaleDateString()}</span>
      </div>
      {invoice.due_date && (
        <div className="flex justify-between text-sm text-gray-600">
          <span>Due Date</span>
          <span>{new Date(invoice.due_date).toLocaleDateString()}</span>
        </div>
      )}

      <Separator />

      {/* Line items */}
      <div className="space-y-3">
        {invoice.line_items?.map((item, i) => (
          <div key={i} className="flex justify-between text-sm">
            <div className="flex-1">
              <span className="text-gray-900">{item.description}</span>
              <span className="text-gray-500 ml-2">
                {parseFloat(item.quantity) !== 1 && `${item.quantity} x $${parseFloat(item.unit_price).toFixed(2)}`}
              </span>
            </div>
            <span className="text-gray-900 font-medium">
              ${parseFloat(item.total_amount).toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      <Separator />

      {/* Totals */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Subtotal</span>
          <span>${parseFloat(invoice.subtotal).toFixed(2)}</span>
        </div>
        {invoice.tax_amount && parseFloat(invoice.tax_amount) > 0 && (
          <div className="flex justify-between text-sm text-gray-600">
            <span>Tax{invoice.tax_rate ? ` (${(parseFloat(invoice.tax_rate) * 100).toFixed(2)}%)` : ''}</span>
            <span>${parseFloat(invoice.tax_amount).toFixed(2)}</span>
          </div>
        )}
        {invoice.discount_amount && parseFloat(invoice.discount_amount) > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount{invoice.discount_description ? ` (${invoice.discount_description})` : ''}</span>
            <span>-${parseFloat(invoice.discount_amount).toFixed(2)}</span>
          </div>
        )}
        <Separator />
        <div className="flex justify-between text-lg font-semibold text-gray-900">
          <span>Total</span>
          <span>${parseFloat(invoice.total_amount).toFixed(2)}</span>
        </div>
      </div>

      {invoice.notes && (
        <>
          <Separator />
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">Notes</p>
            <p>{invoice.notes}</p>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function InvoicePayment() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    async function fetchInvoice() {
      try {
        const res = await fetch(`${API_BASE}/v2/public/invoices/${token}`);
        const json = await res.json();
        if (json.status === 'error') {
          setError(json.message || 'Invoice not found');
        } else {
          setData(json.data);
        }
      } catch {
        setError('Unable to load invoice. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
    fetchInvoice();
  }, [token]);

  const stripePromise = useMemo(() => {
    if (!data?.payment?.publishable_key) return null;
    return loadStripe(data.payment.publishable_key, {
      stripeAccount: data.payment.connected_account_id,
    });
  }, [data?.payment?.publishable_key, data?.payment?.connected_account_id]);

  // --- Loading ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
            <p className="text-gray-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { invoice, payment, status } = data;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          <h1 className="text-xl font-semibold text-gray-900">
            {invoice.business_name}
          </h1>
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <FileText className="h-4 w-4" />
            <span>Invoice {invoice.invoice_number}</span>
            <Badge variant={status === 'paid' ? 'default' : status === 'cancelled' ? 'secondary' : 'outline'}>
              {status}
            </Badge>
          </div>
        </div>

        {/* Invoice details */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Invoice Details</CardTitle>
            <p className="text-sm text-gray-500">For {invoice.customer_name}</p>
          </CardHeader>
          <CardContent>
            <InvoiceDetails invoice={invoice} />
          </CardContent>
        </Card>

        {/* Payment section */}
        <Card>
          <CardContent className="pt-6">
            {status === 'paid' && <PaidScreen invoiceNumber={invoice.invoice_number} />}
            {status === 'cancelled' && <CancelledScreen invoiceNumber={invoice.invoice_number} />}
            {status !== 'paid' && status !== 'cancelled' && !payment.payment_available && (
              <NoPaymentScreen businessName={invoice.business_name} />
            )}
            {status !== 'paid' && status !== 'cancelled' && payment.payment_available && stripePromise && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: payment.client_secret,
                  appearance: {
                    theme: 'stripe',
                    variables: { colorPrimary: '#0f172a' },
                  },
                }}
              >
                <PaymentForm
                  invoiceNumber={invoice.invoice_number}
                  totalAmount={parseFloat(invoice.total_amount).toFixed(2)}
                />
              </Elements>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
